import apiClient, { normalizeApiResponse } from './apiClient';

interface JobProtAuthResponse {
  url: string;
  state: string;
}

interface TokenExchangeRequest {
  code: string;
  state: string;
  referralCode?: string;
}

export interface TokenExchangeResponse {
  user: {
    _id: string;
    email: string;
    name: string;
    profilePicture?: string;
    phoneNumber?: string;
    newUser: boolean;
  };
  access_token: string;
  refresh_token: string;
  // Add for response handling flexibility
  data?: {
    user: {
      _id: string;
      email: string;
      name: string;
      profilePicture?: string;
      phoneNumber?: string;
      newUser: boolean;
    };
    access_token: string;
    refresh_token: string;
  };
  // Add for complete API response structure
  statusCode?: number;
  message?: string;
}

interface SSOPopupCompleteEvent {
  type: 'sso_complete';
  success?: boolean;
  params: {
    redirectUrl: string;
    code?: string;
    clientId?: string;
    state: string;
    isPopup?: boolean;
    success: boolean;
    timestamp: string;
  };
  timestamp?: string;
}

class SSOService {
  /**
   * Initiates JobProt SSO authentication only for login
   * @returns JobProt auth URL and state
   */
  async initiateJobProtSSO(): Promise<JobProtAuthResponse> {
    console.log("Initiating JobProt SSO");
    // This endpoint doesn't require authentication since it's the beginning of the auth flow
    const response = await apiClient.get<any>('/auth/jobprot', undefined, false);
    console.log("Raw API response:", response);
    
    // Handle different response structures
    let url: string;
    let state: string;
    
    if (response.data && typeof response.data === 'object') {
      // We might have a nested response structure like { data: { url, state } }
      url = response.data.url;
      state = response.data.state;
      console.log("Using nested data structure, url:", url, "state:", state);
    } else {
      // Or a direct response like { url, state }
      url = response.url;
      state = response.state;
      console.log("Using direct structure, url:", url, "state:", state);
    }
    
    if (!url || !state) {
      console.error("Failed to extract URL and state from response:", response);
      throw new Error('Invalid authentication response: Missing URL or state');
    }
    
    return { url, state };
  }

  /**
   * Initiates JobProt SSO authentication for signup
   * @param referralCode Optional referral code to use during signup
   * @returns JobProt auth URL and state
   */
  async initiateJobProtSignupSSO(referralCode?: string): Promise<JobProtAuthResponse> {
    console.log("Initiating JobProt SSO");
    // This endpoint doesn't require authentication since it's the beginning of the auth flow
    const response = await apiClient.get<any>(`/auth/jobprot-signup?referralCode=${referralCode}`, undefined, false);
    console.log("Raw API response:", response);
    // Handle different response structures
    let url: string;
    let state: string;
    
    if (response.data && typeof response.data === 'object') {
      // We might have a nested response structure like { data: { url, state } }
      url = response.data.url;
      state = response.data.state;
      console.log("Using nested data structure, url:", url, "state:", state);
    } else {
      // Or a direct response like { url, state }
      url = response.url;
      state = response.state;
      console.log("Using direct structure, url:", url, "state:", state);
    }
    
    if (!url || !state) {
      console.error("Failed to extract URL and state from response:", response);
      throw new Error('Invalid authentication response: Missing URL or state');
    }
    
    return { url, state };
  }

  /**
   * Opens a popup window for SSO authentication
   * @param url Auth URL to open
   * @returns Promise that resolves when SSO is complete
   */
  openSSOPopup(url: string): Promise<SSOPopupCompleteEvent> {
    // This method doesn't make API calls, so no authRequired needed
    return new Promise((resolve, reject) => {
      // Calculate center position for the popup
      const width = 600;
      const height = 700;
      const left = window.innerWidth / 2 - width / 2;
      const top = window.innerHeight / 2 - height / 2;

      // Flag to track if we've received a success message
      let authSuccessReceived = false;

      console.log('Opening SSO popup...');
      
      // Open popup
      const popup = window.open(
        url,
        'JobProt SSO',
        `width=${width},height=${height},left=${left},top=${top},location=yes,toolbar=no,menubar=no`
      );

      if (!popup) {
        reject(new Error('Popup was blocked. Please allow popups for this site.'));
        return;
      }

      // Listen for message from popup - add this BEFORE checking for closure
      const messageListener = (event: MessageEvent) => {
        // Accept messages from JobProt domains or localhost for development
        const isAllowedOrigin = event.origin.includes('jobprot') || 
                               event.origin.includes('localhost');
        
        if (!isAllowedOrigin) {
          console.log(`Ignoring message from non-allowed origin: ${event.origin}`);
          return;
        }

        try {
          console.log('Received message from popup:', event.data);
          
          // Ensure it's a valid message object with the expected structure
          if (!event.data || typeof event.data !== 'object' || !('type' in event.data)) {
            console.log('Received invalid message format:', event.data);
            return;
          }
          
          const data = event.data as SSOPopupCompleteEvent;
          
          // Verify it's an SSO complete event
          if (data.type === 'sso_complete') {
            console.log('Received SSO complete event, success:', data.params.success || data.success);
            
            // Check for success in either the top-level or in params
            const isSuccessful = data.params.success || data.success;
            
            if (isSuccessful) {
              // Set the flag to indicate we've received a success message
              authSuccessReceived = true;
              console.log('Auth success flag set to true');
              
              // Clear interval and event listener
              clearInterval(checkClosed);
              window.removeEventListener('message', messageListener);
              
              // Don't close the popup here - JobProt will close it
              // Just resolve with the SSO data
              resolve(data);
            } else {
              console.log('SSO complete event indicated failure');
              reject(new Error('Authentication failed on JobProt side'));
            }
          }
        } catch (error) {
          console.error('Error processing SSO message:', error);
        }
      };

      // Add event listener for message from popup
      window.addEventListener('message', messageListener);

      // Check if popup was closed without completing SSO
      const checkClosed = setInterval(() => {
        // Check if the popup window exists and is closed
        if (popup.closed) {
          console.log('Popup detected as closed, authSuccessReceived =', authSuccessReceived);
          clearInterval(checkClosed);
          
          // Only reject if we haven't received an auth success message
          if (!authSuccessReceived) {
            console.log('Popup was closed without receiving authentication data');
            
            // Clean up the event listener
            window.removeEventListener('message', messageListener);
            
            reject(new Error('Authentication cancelled.'));
          } else {
            console.log('Popup was closed after receiving authentication data - this is expected');
            // We don't need to do anything here since the success path has already been handled
          }
        }
      }, 1000);
    });
  }

  /**
   * Exchange authorization code for access token
   * @param tokenRequest Token exchange request with code and state
   * @returns Token exchange response with normalized structure
   */
  async exchangeToken(tokenRequest: TokenExchangeRequest): Promise<TokenExchangeResponse> {
    try {
      console.log("Exchanging token with request:", {
        code: tokenRequest.code ? `${tokenRequest.code.substring(0, 10)}...` : null,
        state: tokenRequest.state ? `${tokenRequest.state.substring(0, 10)}...` : null,
        hasReferralCode: !!tokenRequest.referralCode
      });

      console.log("Token request:", tokenRequest);
      
      // This endpoint doesn't require authentication as it's part of the auth flow
      const response = await apiClient.post<any>('/auth/exchange-token', tokenRequest, undefined, false);
      
      // Log the raw response for debugging
      console.log("Raw token exchange response:", response);
      
      // Check for errors in the response
      if (response.error || response.message === 'error') {
        throw new Error(response.error || 'Failed to exchange token');
      }
      
      // Check if we actually got user data and tokens
      if (!response.access_token && !response.data?.access_token) {
        console.error("Missing access token in exchange response:", response);
        throw new Error('Missing access token in authentication response');
      }
      
      // Access token is mandatory for successful login
      const normalizedResponse = normalizeApiResponse<TokenExchangeResponse>(response);
      
      // Log detailed user information
      const user = normalizedResponse.user || normalizedResponse.data?.user;
      if (user) {
        console.log("SSO SERVICE - Token exchange user data details:", {
          id: user._id,
          name: user.name,
          email: user.email,
          profilePictureExists: !!user.profilePicture,
          profilePicture: user.profilePicture,
          allKeys: Object.keys(user)
        });
      }
      
      // Manually check if we have user data after normalization
      if (!normalizedResponse.user && !normalizedResponse.data?.user) {
        console.error("Missing user data in normalized response:", normalizedResponse);
        throw new Error('Missing user data in authentication response');
      }
      
      return normalizedResponse;
    } catch (error: any) {
      console.error("Token exchange failed with error:", error);
      
      // For API error responses, try to extract the message
      if (error.response) {
        const errorMessage = error.response.data?.message || error.message;
        throw new Error(`Token exchange failed: ${errorMessage}`);
      }
      
      // Re-throw the original error with more context
      throw error;
    }
  }

  /**
   * Complete JobProt SSO flow
   * @param referralCode Optional referral code to use during signup
   * @returns User data and tokens
   */
  async completeJobProtSSO(referralCode?: string): Promise<TokenExchangeResponse> {
    try {
      // 1. Initiate SSO and get auth URL
      const { url, state } = await this.initiateJobProtSSO();
      
      // 2. Open popup with auth URL
      const ssoResponse = await this.openSSOPopup(url);
      
      // If referral code is provided, log it
      if (referralCode) {
        console.log(`Using referral code for SSO signup: ${referralCode}`);
      }
      
      // 3. Exchange code for token
      const tokenResponse = await this.exchangeToken({
        code: ssoResponse.params.code || '',
        state: state, // Use original state from the initiateJobProtSSO response
        referralCode
      });
      
      // 4. Return the token response
      return tokenResponse;
    } catch (error) {
      console.error('SSO authentication failed:', error);
      throw error;
    }
  }
}

export const ssoService = new SSOService();
export default ssoService; 